import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/app/global-components/form";
import {
  createTenderQuotationSchema,
  TenderQuotation,
} from "../../../vuetender/components/tenderQuotationSchema";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import QuatationFieldView from "./quatation-field-view";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useTheme,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Button as MuiButton,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { isNull, isEmpty } from "lodash";
import CompanyWarningPopup from "@/app/features/CompanyCreate/companycreatePopup";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import { routes } from "@/consts";
import useItems from "@/hooks/use-item";
import useOffer from "@/hooks/useTender";
import { LANG } from "@/types";
import { ImSpinner2 } from "react-icons/im";
import useRegisterCompany from "@/hooks/use-register-company";
import {
  FileDownIcon,
  Plus,
  ArrowLeft,
  Calculator,
  Package,
  Truck,
  Clock,
  DollarSign,
  Percent,
  FileText,
  CheckCircle,
  Building2,
} from "lucide-react";
import { motion } from "framer-motion";

interface Product {
  productId: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  discount: string;
  notice: string;
  attachment?: File | null;
  images?: string | null | undefined;
}

interface Offer {
  title: string;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
  status: string;
  categories: string[];
  region: string;
  city: string;
  fileName: string;
  fileDescription: string;
  attachmentName: string;
  attachmentDescription: string;
  receiveDocumentsType: string;
  products: ProductOffer[];
}

interface ProductOffer {
  item: string;
  quantity: number;
  unit: string;
  attachment: { url: string } | null;
  notice: string;
}

const QuotationPage = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const tenderQuotationSchema = createTenderQuotationSchema(
    t as (key: string) => string
  );
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const { sendOffertRequest, getOffer } = useOffer();
  const { fetchItems } = useItems();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [offer, setOffer] = useState<Offer | null>(null);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const [activeStep, setActiveStep] = useState(0);

  const [products, setProducts] = useState<Product[]>([
    {
      productId: "",
      unitName: "",
      quantity: 1,
      discount: "",
      unitPrice: 1,
      vat: 0,
      notice: "",
      attachment: null,
      images: null,
    },
  ]);

  const [totalPrice, setTotalPrice] = useState(0);
  const [vat, setVat] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  // Validation state for submit button
  const [isFormValid, setIsFormValid] = useState(false);

  const newProduct: Product = {
    productId: "",
    unitName: "",
    quantity: 0,
    discount: "",
    unitPrice: 1,
    vat: 0,
    notice: "",
    attachment: null,
    images: null,
  };

  const form = useForm<TenderQuotation>({
    resolver: zodResolver(tenderQuotationSchema),
    defaultValues: {
      tenderId: id,
      companyId: myCompany?.id,
      status: "Pending",
      products: [newProduct],
      deliverDays: 0,
      DeadLineDate: "7",
      paymentMethod: "1",
      contactMethod: "1",
      customDeliveryMethod: "",
      OpportunityNr: myCompany?.OpportunityNr + 1,
    },
  });

  const { getCompanyMine } = useRegisterCompany();
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    const loadOffer = async () => {
      try {
        if (id) {
          const offerData = await getOffer(id, myCompany.userId);
          if (offerData) {
            setOffer(offerData.data);
            if (offerData.data.isApplied) {
              navigate(`${routes.vuetender}/${id}`);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching offer:", error);
      }
    };
    if (myCompany?.userId) {
      loadOffer();
    }
  }, [id, myCompany?.userId]);

  useEffect(() => {
    const loadCompanyItems = async () => {
      try {
        if (myCompany?.id) {
          form.setValue("companyId", myCompany.id);
          form.setValue("OpportunityNr", myCompany.OpportunityNr + 1);
          const response = await fetchItems(myCompany.id);
          setItems(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    loadCompanyItems();
  }, [myCompany, form]);

  const onSubmit = async (values: TenderQuotation) => {
    try {
      setIsLoading(true);
      if (values.DeliveryMethod === "Other" && values.customDeliveryMethod) {
        values.DeliveryMethod = values.customDeliveryMethod;
      }
      await sendOffertRequest(values);
      showToast(t("Alert.SubmissionSuccess"), "warning");
      navigate(`${routes.vuetender}/${id}`);
    } catch (err) {
      setIsLoading(false);
      console.error("Error creating offer Quotation:", err);
    }
  };

  useEffect(() => {
    const products = form.getValues("products") ?? [];
    const price = products.reduce((acc, product) => {
      const basePrice = (product.unitPrice ?? 0) * (product.quantity ?? 0);
      const discountAmount =
        basePrice *
        ((product.discount ? parseFloat(product.discount) : 0) / 100);
      const discountedPrice = basePrice - discountAmount;
      const vatAmount = discountedPrice * ((product.vat ?? 0) / 100);
      return acc + discountedPrice + vatAmount;
    }, 0);
    const VAt = products.reduce((acc, product) => {
      const basePrice = (product.unitPrice ?? 0) * (product.quantity ?? 0);
      const discountAmount =
        basePrice *
        ((product.discount ? parseFloat(product.discount) : 0) / 100);
      const discountedPrice = basePrice - discountAmount;
      return acc + discountedPrice * ((product.vat ?? 0) / 100);
    }, 0);
    const totalDiscount = products.reduce((acc, product) => {
      const basePrice = (product.unitPrice ?? 0) * (product.quantity ?? 0);
      return (
        acc +
        basePrice *
          ((product.discount ? parseFloat(product.discount) : 0) / 100)
      );
    }, 0);
    setTotalPrice(price);
    setVat(VAt);
    setTotalDiscount(totalDiscount);
  }, [form.watch("products"), products]);

  // Validate delivery fields for submit button
  useEffect(() => {
    const deliverDays = form.watch("deliverDays");
    const deliveryMethod = form.watch("DeliveryMethod");
    const customDeliveryMethod = form.watch("customDeliveryMethod");

    const hasDeliveryTime = Boolean(deliverDays && deliverDays > 0);
    const hasDeliveryMethod = Boolean(
      deliveryMethod &&
        (deliveryMethod !== "Other" ||
          (deliveryMethod === "Other" &&
            customDeliveryMethod &&
            customDeliveryMethod.trim() !== ""))
    );

    setIsFormValid(hasDeliveryTime && hasDeliveryMethod);
  }, [
    form.watch("deliverDays"),
    form.watch("DeliveryMethod"),
    form.watch("customDeliveryMethod"),
  ]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const addProduct = () => {
    const currentProducts = form.getValues("products") as Product[];
    const updatedProducts = [...currentProducts, newProduct];
    setProducts(updatedProducts);
    form.setValue("products", updatedProducts);
  };

  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("products") as Product[];
    const newProducts = currentProducts.filter((_, i) => i !== index);
    setProducts(newProducts);
    form.setValue("products", newProducts);
  };

  const options = [
    { value: "7", label: `7 ${t("time.days")}` },
    { value: "14", label: `14 ${t("time.days")}` },
    { value: "30", label: `30 ${t("time.days")}` },
  ];

  const optionsDelievery = [
    { value: "FedEx", label: "FedEx" },
    { value: "DHL Express", label: "DHL Express" },
    { value: "SPL", label: "سُبل" },
    { value: "Other", label: t("delivery.other") },
  ];

  const columns = [
    {
      title: "",
      field: "image",
      render: (row: Product) =>
        row.images ? (
          <Avatar
            src={row.images}
            alt="item"
            sx={{ width: 56, height: 56, mx: "auto" }}
          />
        ) : (
          <Avatar
            sx={{ width: 56, height: 56, mx: "auto", bgcolor: "grey.300" }}
          >
            <Package size={24} />
          </Avatar>
        ),
    },
    {
      title: t("checkout.fields.name"),
      field: "unitName",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.productId`}
            placeholder={t("offers.placeholders.EnterItemName")}
            label=""
            inputType="select"
            formControl={form.control}
            options={[
              ...items.map((item) => ({ value: item.id, label: item.name })),
            ]}
            onChange={(value) => {
              const selectedItem = items.find((item) => item.id === value);
              if (selectedItem) {
                const updatedProducts = [...products];
                updatedProducts[rowIndex] = {
                  ...updatedProducts[rowIndex],
                  productId: selectedItem.id,
                  unitName: selectedItem.name,
                  unitPrice: selectedItem.price,
                  vat: selectedItem.vat,
                  images: selectedItem.images?.[0],
                };

                form.setValue("products", updatedProducts);
                setProducts(updatedProducts);
              }
            }}
          />
        );
      },
    },
    {
      title: t("checkout.fields.quantity"),
      field: "quantity",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.quantity`}
            placeholder={t("offers.placeholders.EnterQuantity")}
            label=""
            inputType="number"
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[rowIndex].quantity =
                parseInt(parsedValue, 10) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("checkout.fields.unitPrice"),
      field: "unitPrice",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.unitPrice`}
            placeholder={t("quatation.placeholders.EnterPrice")}
            label=""
            inputType="number"
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[rowIndex].unitPrice =
                parseFloat(parsedValue) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("orders.fields.discount"),
      field: "discount",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.discount`}
            placeholder={""}
            label=""
            inputType="number"
            formControl={form.control}
            onChange={(value) => {
              const discount =
                typeof value === "string" ? value : value.target.value;
              const updatedProducts = [...products];
              if (+discount > 100) {
                updatedProducts[rowIndex].discount = "99";
              } else {
                updatedProducts[rowIndex].discount = discount;
              }
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("item.fields.vat"),
      field: "vat",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.vat`}
            placeholder={t("quatation.placeholders.EnterVAT")}
            label=""
            inputType="select"
            options={[
              { value: "0", label: "0%" },
              { value: "15", label: "15%" },
            ]}
            formControl={form.control}
            onChange={(value) => {
              const updatedProducts = [...products];
              const parsedValue =
                typeof value === "string" ? value : value.target.value;
              updatedProducts[rowIndex].vat = parseFloat(parsedValue) || 0;
              setProducts(updatedProducts);
              form.setValue("products", updatedProducts);
            }}
          />
        );
      },
    },
    {
      title: t("orders.fields.notes"),
      field: "notice",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.notice`}
            placeholder={t("tender.fields.EnterNote")}
            label=""
            inputType="textarea"
            formControl={form.control}
          />
        );
      },
    },
    {
      title: t("attachments.title"),
      field: "attachment",
      render: (_: Product, rowIndex: number) => {
        return (
          <QuatationFieldView
            name={`products.${rowIndex}.attachment`}
            placeholder={t("offers.placeholders.SelectFile")}
            label=""
            inputType="file"
            formControl={form.control}
          />
        );
      },
    },
    {
      title: t("actions.Action"),
      field: "action",
      render: (_: Product, rowIndex: number) => {
        return (
          <IconButton
            onClick={() => removeProduct(rowIndex)}
            sx={{
              color: "#ef4444",
              "&:hover": {
                backgroundColor: "#fef2f2",
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        );
      },
    },
  ];

  const watchedProducts = form.watch("products") || [];

  const steps = [
    {
      label: t("offers.items"),
      icon: <Package size={20} />,
      content: (
        <Box sx={{ mt: 2 }}>
          {!isNull(offer) &&
            !isEmpty(offer) &&
            offer.products &&
            offer.products.length > 0 && (
              <Card
                sx={{ mb: 3, borderRadius: 2, border: 0, boxShadow: "none" }}
              >
                <CardContent>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 16,
                      backgroundColor: "#dbeafe",
                      borderRadius: 12,
                      marginBottom: 24,
                    }}
                  >
                    <FileText size={20} style={{ color: "#644980" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("offers.items")}
                    </Typography>
                  </motion.div>
                  <TableContainer
                    component={Paper}
                    sx={{ borderRadius: 2, boxShadow: "none" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "grey.50" }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("offers.item")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("checkout.fields.quantity")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("offers.attachment")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t("orders.fields.notes")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {offer.products.map((product, index) => (
                          <TableRow
                            key={index}
                            sx={{ "&:hover": { backgroundColor: "grey.50" } }}
                          >
                            <TableCell>{product.item}</TableCell>
                            <TableCell>
                              {product.quantity} {product.unit}
                            </TableCell>
                            <TableCell>
                              {product.attachment ? (
                                <Link
                                  to={product.attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconButton
                                    sx={{
                                      backgroundColor: "primary.main",
                                      color: "white",
                                      "&:hover": {
                                        backgroundColor: "primary.dark",
                                      },
                                    }}
                                  >
                                    <FileDownIcon size={20} />
                                  </IconButton>
                                </Link>
                              ) : (
                                <Chip
                                  label={t("general.Unavailabe")}
                                  size="small"
                                  color="default"
                                />
                              )}
                            </TableCell>
                            <TableCell>{product.notice}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

          <Card sx={{ borderRadius: 2, border: 0, boxShadow: "none" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#f0fdf4",
                    borderRadius: 12,
                  }}
                >
                  <Package size={20} style={{ color: "#16a34a" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("products.list")}
                  </Typography>
                </motion.div>
                <MuiButton
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={addProduct}
                  sx={{
                    background: "#18181b",
                  }}
                >
                  {t("products.add")}
                </MuiButton>
              </Box>

              <TableContainer
                component={Paper}
                sx={{ borderRadius: 2, boxShadow: "none" }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      {columns.map((column, index) => (
                        <TableCell key={index} sx={{ fontWeight: 600 }}>
                          {column.title}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {watchedProducts.length > 0 ? (
                      watchedProducts.map((row, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{ "&:hover": { backgroundColor: "grey.50" } }}
                        >
                          {columns.map((column, columnIndex) => (
                            <TableCell key={columnIndex}>
                              {column.render
                                ? column.render(row, rowIndex)
                                : (row as any)[column.field]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {t("general.NoDataAvailable")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      ),
    },
    {
      label: t("delivery.methods"),
      icon: <Truck size={20} />,
      content: (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  border: 0,
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 16,
                      backgroundColor: "#fef3c7",
                      borderRadius: 12,
                      marginBottom: 24,
                    }}
                  >
                    <Clock size={20} style={{ color: "#d97706" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("delivery.suggestedTime")}
                    </Typography>
                  </motion.div>
                  <QuatationFieldView
                    name="deliverDays"
                    placeholder=""
                    label={`${t("delivery.suggestedTime")} (${t("time.days")})`}
                    inputType="number"
                    formControl={form.control}
                  />
                  <QuatationFieldView
                    name="DeadLineDate"
                    placeholder=""
                    label={t("offers.validityPeriod1")}
                    inputType="select"
                    options={options}
                    formControl={form.control}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  border: 0,
                  boxShadow: "none",
                }}
              >
                <CardContent>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 16,
                      backgroundColor: "#eff6ff",
                      borderRadius: 12,
                      marginBottom: 24,
                    }}
                  >
                    <Truck size={20} style={{ color: "#644980" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("delivery.methods")}
                    </Typography>
                  </motion.div>
                  <QuatationFieldView
                    name="DeliveryMethod"
                    placeholder=""
                    label={t("delivery.methods")}
                    inputType="radio"
                    options={optionsDelievery}
                    formControl={form.control}
                    onChange={(value) => {
                      if (value === "Other") {
                        form.setValue("customDeliveryMethod", "");
                      } else {
                        form.setValue("customDeliveryMethod", "");
                      }
                    }}
                  />
                  {form.watch("DeliveryMethod") === "Other" && (
                    <QuatationFieldView
                      name="customDeliveryMethod"
                      placeholder={t("delivery.enterMethod")}
                      label={t("delivery.customMethod")}
                      inputType="text"
                      formControl={form.control}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ];

  // Step navigation functions
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  return (
    <Box
      dir={locale === LANG.AR ? "rtl" : "ltr"}
      sx={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: {
          xs: "12px 16px",
          sm: "16px 20px",
          md: "20px 24px",
          lg: "24px 32px",
        },
      }}
    >
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}

      <Container maxWidth={false} sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <MuiButton
              variant="outlined"
              startIcon={<ArrowLeft size={20} />}
              onClick={handleGoBack}
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              {t("actions.cancel")}
            </MuiButton>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2937" }}>
              {t("actions.addTenderRequest")}
            </Typography>
          </Box>

          {/* Company Info & Estimated Value */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 16,
                  backgroundColor: "#eff6ff",
                  borderRadius: 12,
                }}
              >
                <Building2 size={20} style={{ color: "#644980" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("company.fields.name")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {myCompany?.CompanyNr}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 16,
                  backgroundColor: "#f0fdf4",
                  borderRadius: 12,
                }}
              >
                <DollarSign size={20} style={{ color: "#16a34a" }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("tender.fields.estimatedValue")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {offer?.value ?? "-"} {t("currency")}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Progress Stepper */}
          <Card sx={{ borderRadius: 2, mb: 3, border: 0, boxShadow: "none" }}>
            <CardContent>
              <Stepper activeStep={activeStep} orientation="horizontal">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel
                      icon={step.icon}
                      onClick={() => handleStepClick(index)}
                      sx={{
                        cursor: "pointer",
                        "& .MuiStepLabel-label": {
                          fontWeight: 600,
                        },
                        "&:hover": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Box>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {/* Step Content */}
            <Card sx={{ borderRadius: 2, mb: 3, border: 0, boxShadow: "none" }}>
              <CardContent>{steps[activeStep].content}</CardContent>
            </Card>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#fef3c7",
                    borderRadius: 12,
                  }}
                >
                  <Percent size={20} style={{ color: "#d97706" }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("orders.fields.discount")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {totalDiscount.toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#dbeafe",
                    borderRadius: 12,
                  }}
                >
                  <Calculator size={20} style={{ color: "#644980" }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("item.fields.vat")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {vat.toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#f0fdf4",
                    borderRadius: 12,
                  }}
                >
                  <DollarSign size={20} style={{ color: "#16a34a" }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("orders.fields.totalPrice")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {totalPrice.toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 16,
                    backgroundColor: "#eff6ff",
                    borderRadius: 12,
                  }}
                >
                  <Clock size={20} style={{ color: "#644980" }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("offers.validityPeriod1")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {form.watch("DeadLineDate")} {t("time.days")}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>

            {/* Terms and Conditions */}
            <Card sx={{ borderRadius: 2, mb: 3, border: 0, boxShadow: "none" }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      sx={{
                        color: "#6b7280",
                        "&.Mui-checked": {
                          color: "#644980",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {t("general.AgreeToTerms")}
                    </Typography>
                  }
                  sx={{ margin: 0 }}
                />
              </CardContent>
            </Card>

            {/* Action Buttons with Navigation */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left side - Navigation */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <MuiButton
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{
                    borderColor: "#d1d5db",
                    color: "#6b7280",
                    "&:hover": {
                      borderColor: "#9ca3af",
                      backgroundColor: "#f3f4f6",
                    },
                    "&:disabled": {
                      borderColor: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {t("actions.previousStep")}
                </MuiButton>

                <Box sx={{ display: "flex", gap: 1 }}>
                  {steps.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor:
                          index === activeStep
                            ? theme.palette.primary.main
                            : theme.palette.grey[300],
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor:
                            index === activeStep
                              ? theme.palette.primary.dark
                              : theme.palette.grey[400],
                        },
                      }}
                      onClick={() => handleStepClick(index)}
                    />
                  ))}
                </Box>

                <MuiButton
                  variant="outlined"
                  onClick={handleNext}
                  disabled={activeStep === steps.length - 1}
                  sx={{
                    borderColor: "#18181b",
                    color: "#18181b",

                    "&:disabled": {
                      borderColor: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {t("actions.nextStep")}
                </MuiButton>
              </Box>

              {/* Right side - Main Actions */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <MuiButton
                  variant="outlined"
                  onClick={handleGoBack}
                  sx={{
                    borderColor: "#d1d5db",
                    color: "#6b7280",
                    "&:hover": {
                      borderColor: "#9ca3af",
                      backgroundColor: "#f3f4f6",
                    },
                  }}
                >
                  {t("actions.cancel")}
                </MuiButton>
                <MuiButton
                  variant="contained"
                  type="submit"
                  disabled={!acceptTerms || isLoading || !isFormValid}
                  startIcon={
                    isLoading ? (
                      <ImSpinner2 className="animate-spin" />
                    ) : (
                      <CheckCircle size={20} />
                    )
                  }
                  sx={{
                    background: "#18181b",
                    color: "white",

                    "&:disabled": {
                      background: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {isLoading ? t("actions.submitting") : t("actions.submit1")}
                </MuiButton>
              </Box>
            </Box>
          </form>
        </Form>
      </Container>
    </Box>
  );
};

export default QuotationPage;
